// src/features/monetize/MonetizeUtils.ts

import { DateTime } from 'luxon';
import { ApiError } from '@/shared/api/ApiError';
import { PICKER_DATE_FORMAT } from '@/shared/utils/date';
import {
  ACCESS_MODE_LABELS,
  AUDIT_ACTION_LABELS,
  MONETIZE_CONSTRAINTS,
  MONETIZE_CURRENCY,
  PRICE_INTERVAL_LABELS,
  PURCHASE_STATUS_LABELS,
  SCOPE_TYPES,
  SUBSCRIPTION_STATUS_LABELS,
} from '@/lib/constants/monetize';
import type {
  CancelSubscriptionReq,
  CompReq,
  TrialConversion,
  GatingRule,
  MonetizeAuditEntry,
  MonetizeEntitlement,
  MonetizeOffer,
  MonetizePrice,
  MonetizeProduct,
  MonetizePurchase,
  MonetizeSubscription,
  OfferReq,
  OfferUpdateReq,
  PriceInterval,
  ProductReq,
  ProductType,
  RefundReq,
} from '@/lib/types/Monetize';
import type { CancelFormValues, CompFormValues, OfferFormValues, PriceFormValues, ProductFormValues, RefundFormValues } from './schemas/monetizeSchemas';

export type ScopeType = (typeof SCOPE_TYPES)[number];

export interface ParsedScope {
  type: ScopeType;
  id: number;
}

/** A media candidate for the bounded scope search (title index built once). */
export interface MediaCandidate {
  id: number;
  title: string;
  titleLower: string;
  live: boolean;
}

export class MonetizeUtils {
  /** The preview URL with the panel's preview pass on it (Seq 1 step 8); unchanged without one. */
  static withPreviewGrant(url: string, grant: string | null): string {
    if (!grant) return url;
    return `${url}${url.includes('?') ? '&' : '?'}grant=${encodeURIComponent(grant)}`;
  }

  /** Cents → a currency string in the user's locale ("$9.99"). */
  static formatAmount(cents: number, currency: string = MONETIZE_CURRENCY): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
  }

  /** A NumberInput value in dollars → cents; NaN for an empty or invalid value. */
  static dollarsToCents(value: number | string): number {
    const n = typeof value === 'number' ? value : Number.parseFloat(value);
    return Number.isFinite(n) ? Math.round(n * 100) : NaN;
  }

  static centsToDollars(cents: number): number {
    return Math.round(cents) / 100;
  }

  /** Basis points → "2.5 %". */
  static feeLabel(bps: number): string {
    return `${(bps / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })} %`;
  }

  /** RFC 3339 (UTC) → the DateTimePicker's local string; null when absent or invalid. */
  static isoToPicker(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.toFormat(PICKER_DATE_FORMAT) : null;
  }

  /** The DateTimePicker's local string → RFC 3339 in UTC; null when empty or invalid. */
  static pickerToIso(value: string | null | undefined): string | null {
    if (!value) return null;
    const dt = DateTime.fromFormat(value, PICKER_DATE_FORMAT);
    return dt.isValid ? dt.toUTC().toISO({ suppressMilliseconds: true }) : null;
  }

  /** RFC 3339 → a short local date-time for tables ("Oct 18, 7:00 PM"). */
  static formatWhen(iso: string | null | undefined): string {
    if (!iso) return '';
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.toLocaleString({ month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  }

  static parseScope(token: string): ParsedScope | null {
    const i = token.indexOf(':');
    if (i <= 0) return null;
    const type = token.slice(0, i) as ScopeType;
    const id = Number(token.slice(i + 1));
    if (!SCOPE_TYPES.includes(type) || !Number.isInteger(id) || id <= 0) return null;
    return { type, id };
  }

  static scopeToken(type: ScopeType, id: number): string {
    return `${type}:${id}`;
  }

  /**
   * A readable scope: the list or media title when the lookups know it,
   * otherwise the kind and id. The whole service reads as such.
   */
  static scopeLabel(token: string, lookups: { lists: Map<number, string>; media: Map<number, string> }): string {
    const scope = this.parseScope(token);
    if (!scope) return token;
    switch (scope.type) {
      case 'service':
        return 'Everything you publish';
      case 'list':
        return lookups.lists.get(scope.id) ?? `Playlist #${scope.id}`;
      case 'media':
        return lookups.media.get(scope.id) ?? `Media #${scope.id}`;
    }
  }

  /** "$9.99 / month", "$79.00 / year · 7-day trial", "$14.99". */
  static priceLabel(price: Pick<MonetizePrice, 'unitAmount' | 'currency' | 'interval' | 'trialDays'>): string {
    const amount = this.formatAmount(price.unitAmount, price.currency);
    const recurring = price.interval === 'month' ? `${amount} / month` : price.interval === 'year' ? `${amount} / year` : amount;
    return price.trialDays > 0 ? `${recurring} · ${price.trialDays}-day trial` : recurring;
  }

  /** The active prices of a product as one line; "No price" when none. */
  static priceSummary(product: MonetizeProduct): string {
    const active = product.prices.filter((p) => p.status === 'active');
    return active.length === 0 ? 'No price' : active.map((p) => this.priceLabel(p)).join(' · ');
  }

  static intervalsFor(type: ProductType): PriceInterval[] {
    return type === 'subscription' ? ['month', 'year'] : ['one_time'];
  }

  static intervalLabel(interval: PriceInterval): string {
    return PRICE_INTERVAL_LABELS[interval];
  }

  /** "Half off" → "50% off"; "$3.00 off"; with the duration when it recurs. */
  static discountLabel(offer: MonetizeOffer): string {
    const base = offer.percentOff ? `${offer.percentOff}% off` : `${this.formatAmount(offer.amountOff ?? 0)} off`;
    switch (offer.duration) {
      case 'repeating':
        return `${base} for ${offer.durationMonths} months`;
      case 'forever':
        return `${base}, every payment`;
      default:
        return base;
    }
  }

  /** Word-substring search over media titles, bounded (the options are mounted in a Select). */
  static searchMedia(candidates: MediaCandidate[], exclude: ReadonlySet<number>, search: string, maxOptions: number): { value: string; label: string }[] {
    const terms = search.toLowerCase().trim().split(' ').filter(Boolean);
    const out: { value: string; label: string }[] = [];
    for (const c of candidates) {
      if (exclude.has(c.id)) continue;
      if (terms.length > 0 && !terms.every((t) => c.titleLower.includes(t))) continue;
      out.push({ value: String(c.id), label: c.live ? `${c.title} (live)` : c.title });
      if (out.length === maxOptions) break;
    }
    return out;
  }

  /** How many items and lists the catalog gates, for the page header. */
  static gatingSummary(gating: GatingRule[]): string {
    if (gating.length === 0) return 'Nothing is gated yet.';
    const counts = { media: 0, list: 0, service: 0 };
    for (const g of gating) {
      const s = this.parseScope(g.scope);
      if (s) counts[s.type] += 1;
    }
    if (counts.service > 0) return 'Everything you publish is behind a purchase.';
    const parts: string[] = [];
    if (counts.list > 0) parts.push(`${counts.list} playlist${counts.list === 1 ? '' : 's'}`);
    if (counts.media > 0) parts.push(`${counts.media} item${counts.media === 1 ? '' : 's'}`);
    return `Behind a purchase: ${parts.join(' and ')}.`;
  }

  static defaultProductForm(type: ProductType = 'subscription'): ProductFormValues {
    return {
      type,
      name: '',
      description: '',
      active: true,
      wholeService: false,
      scopes: [],
      accessStartsAt: null,
      accessEndsAt: null,
      rentalWindowHours: type === 'rental' ? 48 : '',
      onSaleFrom: null,
      onSaleUntil: null,
      maxConcurrentStreams: MONETIZE_CONSTRAINTS.DEFAULT_CONCURRENT,
      prices: [this.defaultPriceForm(type)],
    };
  }

  static defaultPriceForm(type: ProductType): PriceFormValues {
    return { amount: '', interval: type === 'subscription' ? 'month' : 'one_time', trialDays: 0, nickname: '' };
  }

  static productToForm(product: MonetizeProduct): ProductFormValues {
    const wholeService = product.scopes.some((s) => s.startsWith('service:'));
    return {
      type: product.type,
      name: product.name,
      description: product.description,
      active: product.status === 'active',
      wholeService,
      scopes: wholeService ? [] : [...product.scopes],
      accessStartsAt: this.isoToPicker(product.accessStartsAt),
      accessEndsAt: this.isoToPicker(product.accessEndsAt),
      rentalWindowHours: product.rentalWindowSeconds ? Math.round(product.rentalWindowSeconds / 3600) : '',
      onSaleFrom: this.isoToPicker(product.onSaleFrom),
      onSaleUntil: this.isoToPicker(product.onSaleUntil),
      maxConcurrentStreams: product.maxConcurrentStreams,
      // prices are managed row by row once the product exists
      prices: [],
    };
  }

  /** The create / update body. On an update the server ignores status and prices (their own operations). */
  static formToProductReq(values: ProductFormValues, cId: number, includePrices: boolean): ProductReq {
    const hours = typeof values.rentalWindowHours === 'number' ? values.rentalWindowHours : 0;
    return {
      type: values.type,
      name: values.name.trim(),
      description: values.description.trim(),
      status: values.active ? 'active' : 'draft',
      scopes: values.wholeService ? [this.scopeToken('service', cId)] : values.scopes,
      accessStartsAt: values.type === 'pass' ? this.pickerToIso(values.accessStartsAt) : null,
      accessEndsAt: values.type === 'pass' ? this.pickerToIso(values.accessEndsAt) : null,
      rentalWindowSeconds: values.type === 'rental' ? hours * 3600 : 0,
      onSaleFrom: this.pickerToIso(values.onSaleFrom),
      onSaleUntil: this.pickerToIso(values.onSaleUntil),
      maxConcurrentStreams: values.maxConcurrentStreams,
      sortOrder: 0,
      prices: includePrices ? values.prices.map((p) => this.formToPriceReq(p)) : [],
    };
  }

  static formToPriceReq(price: PriceFormValues) {
    return {
      currency: MONETIZE_CURRENCY,
      unitAmount: this.dollarsToCents(price.amount),
      interval: price.interval,
      trialDays: price.interval === 'one_time' ? 0 : price.trialDays,
      nickname: price.nickname.trim(),
    };
  }

  static defaultOfferForm(): OfferFormValues {
    return {
      code: '',
      name: '',
      discountType: 'percent',
      percentOff: 10,
      amountOff: '',
      duration: 'once',
      durationMonths: 3,
      productId: '',
      startsAt: null,
      endsAt: null,
      maxRedemptions: '',
    };
  }

  static offerToForm(offer: MonetizeOffer): OfferFormValues {
    return {
      code: offer.code,
      name: offer.name,
      discountType: offer.percentOff ? 'percent' : 'amount',
      percentOff: offer.percentOff ?? 0,
      amountOff: offer.amountOff ? this.centsToDollars(offer.amountOff) : '',
      duration: offer.duration,
      durationMonths: offer.durationMonths ?? 3,
      productId: offer.productId ?? '',
      startsAt: this.isoToPicker(offer.startsAt),
      endsAt: this.isoToPicker(offer.endsAt),
      maxRedemptions: offer.maxRedemptions ?? '',
    };
  }

  static formToOfferReq(values: OfferFormValues): OfferReq {
    return {
      code: values.code.trim().toUpperCase(),
      name: values.name.trim(),
      percentOff: values.discountType === 'percent' ? values.percentOff : 0,
      amountOff: values.discountType === 'amount' ? this.dollarsToCents(values.amountOff) : 0,
      duration: values.duration,
      durationMonths: values.duration === 'repeating' ? values.durationMonths : 0,
      productId: values.productId,
      startsAt: this.pickerToIso(values.startsAt),
      endsAt: this.pickerToIso(values.endsAt),
      maxRedemptions: typeof values.maxRedemptions === 'number' ? values.maxRedemptions : 0,
    };
  }

  static formToOfferUpdateReq(values: OfferFormValues): OfferUpdateReq {
    return {
      name: values.name.trim(),
      productId: values.productId,
      startsAt: this.pickerToIso(values.startsAt),
      endsAt: this.pickerToIso(values.endsAt),
      maxRedemptions: typeof values.maxRedemptions === 'number' ? values.maxRedemptions : 0,
    };
  }

  // ---- the dashboard (Seq 1 step 7) ----

  static formatCount(n: number): string {
    return new Intl.NumberFormat('en-US').format(n);
  }

  /** `part` as a share of `whole`, or empty when there is no whole. */
  static percent(part: number, whole: number): string | null {
    if (whole <= 0) return null;
    return `${Math.round((part / whole) * 100)} %`;
  }

  static trialConversionLabel(t: TrialConversion): string | null {
    const decided = t.converted + t.lapsed;
    if (t.trials === 0) return null;
    if (decided === 0) return 'Still running';
    return `${Math.round((t.converted / decided) * 100)} %`;
  }

  /** A short tick label for a UTC day (YYYY-MM-DD). */
  static dayLabel(day: string): string {
    const d = DateTime.fromISO(day, { zone: 'utc' });
    return d.isValid ? d.toFormat('MMM d') : day;
  }

  static rangeLabel(from: string, to: string): string {
    const a = DateTime.fromISO(from, { zone: 'utc' });
    const b = DateTime.fromISO(to, { zone: 'utc' });
    if (!a.isValid || !b.isValid) return `${from} – ${to}`;
    return `${a.toFormat('MMM d')} – ${b.toFormat(a.year === b.year ? 'MMM d' : 'MMM d, yyyy')}`;
  }

  // ---- the viewer's ledger (Seq 1 step 4) ----

  static accessModeLabel(mode: string): string {
    return (ACCESS_MODE_LABELS as Record<string, string>)[mode] ?? mode;
  }

  static entitlementSourceLabel(e: Pick<MonetizeEntitlement, 'sourceType'>): string {
    switch (e.sourceType) {
      case 'purchase':
        return 'Purchase';
      case 'subscription':
        return 'Subscription';
      case 'manual':
        return 'Granted by you';
      case 'system':
        return 'Automatic';
      default:
        return e.sourceType;
    }
  }

  static entitlementWindow(e: Pick<MonetizeEntitlement, 'startsAt' | 'endsAt'>): string {
    return `${this.formatWhen(e.startsAt)} – ${e.endsAt ? this.formatWhen(e.endsAt) : 'no end'}`;
  }

  static auditActionLabel(action: string): string {
    return AUDIT_ACTION_LABELS[action] ?? action;
  }

  static actorLabel(entry: Pick<MonetizeAuditEntry, 'actorType' | 'actorId'>): string {
    switch (entry.actorType) {
      case 'cpuser':
        return `Team member ${entry.actorId}`;
      case 'ops':
        return 'Support';
      case 'system':
        return 'Automatic';
      default:
        return `${entry.actorType} ${entry.actorId}`;
    }
  }

  static purchaseStatusLabel(p: Pick<MonetizePurchase, 'status' | 'refundedAmount' | 'currency'>): string {
    const base = (PURCHASE_STATUS_LABELS as Record<string, string>)[p.status] ?? p.status;
    if (p.status === 'completed' && p.refundedAmount > 0) return `${base} · ${this.formatAmount(p.refundedAmount, p.currency)} refunded`;
    return base;
  }

  /** One line on a subscription: its state and the date that matters next. */
  static subscriptionSummary(s: Pick<MonetizeSubscription, 'status' | 'currentPeriodEnd' | 'cancelAtPeriodEnd' | 'trialEnd' | 'canceledAt'>): string {
    const label = (SUBSCRIPTION_STATUS_LABELS as Record<string, string>)[s.status] ?? s.status;
    switch (s.status) {
      case 'trialing': {
        const firstCharge = s.trialEnd ?? s.currentPeriodEnd;
        if (s.cancelAtPeriodEnd) return `${label} · ends ${firstCharge ? this.formatWhen(firstCharge) : 'at the trial end'}`;
        return firstCharge ? `${label} · first charge ${this.formatWhen(firstCharge)}` : label;
      }
      case 'active':
        if (!s.currentPeriodEnd) return label;
        return s.cancelAtPeriodEnd ? `${label} · ends ${this.formatWhen(s.currentPeriodEnd)}` : `${label} · renews ${this.formatWhen(s.currentPeriodEnd)}`;
      case 'past_due':
        return `${label} · payment being retried`;
      case 'paused':
        return `${label} · no payment method after the trial`;
      case 'canceled':
        return s.canceledAt ? `${label} ${this.formatWhen(s.canceledAt)}` : label;
      default:
        return label;
    }
  }

  static defaultCompForm(): CompFormValues {
    return { wholeService: false, scopes: [], accessMode: 'manual_comp', endsAt: null, reason: '' };
  }

  /** The grants a comp form asks for: one per scope, or the whole service. */
  static formToComp(values: CompFormValues, cId: number): { scopes: string[]; req: Omit<CompReq, 'scope'> } {
    return {
      scopes: values.wholeService ? [this.scopeToken('service', cId)] : values.scopes,
      req: { accessMode: values.accessMode, startsAt: null, endsAt: this.pickerToIso(values.endsAt), reason: values.reason.trim() },
    };
  }

  static defaultRefundForm(): RefundFormValues {
    return { mode: 'full', amount: '', reason: '' };
  }

  static formToRefundReq(values: RefundFormValues): RefundReq {
    return { amount: values.mode === 'partial' ? this.dollarsToCents(values.amount) : 0, reason: values.reason.trim() };
  }

  static defaultCancelForm(): CancelFormValues {
    return { when: 'period_end', refundLastPayment: false, reason: '' };
  }

  static formToCancelReq(values: CancelFormValues): CancelSubscriptionReq {
    return { atPeriodEnd: values.when === 'period_end', refundLastPayment: values.refundLastPayment, reason: values.reason.trim() };
  }

  /** The message to show for a failed write: the server's field problems when it sent them. */
  static errorMessage(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      if (err.problems && err.problems.length > 0) {
        return err.problems.map((p) => `${p.field}: ${p.message}`).join('; ');
      }
      return err.message || fallback;
    }
    return err instanceof Error && err.message ? err.message : fallback;
  }
}
